// 当前用户上下文 composable (V2 权限基础设施 M1a)
//
// 设计:
//   - 真实登录身份、角色、动作权限和数据范围全部以 userStore (/auth/me) 为唯一来源
//   - 禁止再用 userRoleStore 的 E0001 等模拟账号拼装生产权限上下文
//   - 角色/身份切换由 userStore 重签 token、刷新 /auth/me 后整页重载
//   - cacheVersion 触发响应式更新

import { reactive, computed } from 'vue'
import { tempGrantApi } from '@/api'
import { useUserStore } from '@/stores/user'
import type { ModulePermission } from '@/stores/permissionMatrixStore'
import { fieldPermissionStore, type FieldRule } from '@/stores/fieldPermissionStore'
import type { TempGrant } from '@/stores/tempGrantStore'

// ─── 类型 ─────────────────────────────────────────────────────────────────
export interface CurrentUserDataScope {
  centers: string[]
  factories: string[]
  depts: string[]
  legalEntities: string[]
  empNos?: string[]
  managerEmpNo?: string
  treeDepth?: number
  projectIds?: string[]
  orgIds?: number[]
  empIds?: number[]
}

export interface CurrentUserContext {
  empNo: string
  empName: string
  email?: string
  primaryRoleCode: string
  roleCodes: string[]
  assignments: []
  modulePermissions: Record<string, ModulePermission[]>  // module → permissions
  fieldPermissions: FieldRule[]
  dataScope: CurrentUserDataScope
  tempGrants: TempGrant[]
  isBreakGlassActive: boolean
  breakGlass?: any
  cacheVersion: number
}

// ─── State ────────────────────────────────────────────────────────────────
const state = reactive<{ cacheVersion: number }>({ cacheVersion: 0 })

function currentToken(): string {
  // lazy + try-catch: pinia 未安装时 (模块顶层求值早于 app.use) 不崩, 下次求值重试
  try { return useUserStore().token || '' } catch { return '' }
}

// BreakGlass 状态只认后端真记录，localStorage seed 不参与鉴权和横幅。
const bgState = reactive<{ active: any | null; fetchedForToken: string | null; inFlight: boolean }>({
  active: null, fetchedForToken: null, inFlight: false,
})

function ensureBreakGlassState(): void {
  const token = currentToken()
  if (!token) {
    bgState.active = null
    bgState.fetchedForToken = null
    return
  }
  if (bgState.inFlight || bgState.fetchedForToken === token) return
  bgState.inFlight = true
  tempGrantApi.breakGlass.mineActive()
    .then((row: any) => { bgState.active = row || null })
    .catch(() => { bgState.active = null })
    .finally(() => {
      bgState.fetchedForToken = token
      bgState.inFlight = false
      state.cacheVersion++
    })
}

// ─── 上下文计算 ─────────────────────────────────────────────────────────
const context = computed<CurrentUserContext>(() => {
  void state.cacheVersion  // 用于触发刷新

  let authUser: ReturnType<typeof useUserStore>['user'] = null
  try { authUser = useUserStore().user } catch { /* Pinia 尚未初始化 */ }
  const empNo = authUser?.username || (authUser?.empId != null ? String(authUser.empId) : '')
  const empName = authUser?.nickname || authUser?.username || ''
  const effectiveRoleCode = authUser?.effectiveRoleCode || authUser?.roles?.[0] || ''
  const primaryRoleCode = authUser?.primaryRoleCode || effectiveRoleCode
  // SecurityUser.roles 已被后端收敛为当前真正生效的单一角色，禁止在前端恢复角色并集。
  const roleCodes = effectiveRoleCode ? [effectiveRoleCode] : []
  const assignments: [] = []

  // 临时授权和 BreakGlass 均由后端并入 SecurityUser；本地 seed 不得进入当前用户上下文。
  const tempGrants: TempGrant[] = []

  // 模块权限直接从 /auth/me 的当前生效角色权限构造；禁止恢复多角色权限并集。
  // 每模块聚合一条; 'emp:read' → module='emp', actions.read=true; 后端 'batch' 同时点亮前端别名 'bulk';
  // dataScope 给 'ALL' 占位 (真实数据范围约束在后端 @DataScope, 前端不再据此过滤)
  const modulePermissions: Record<string, ModulePermission[]> = {}
  const grouped: Record<string, Record<string, boolean>> = {}
  for (const c of authUser?.perms || []) {
    const i = c.indexOf(':')
    if (i <= 0) continue
    const m = c.slice(0, i)
    const a = c.slice(i + 1)
    if (!grouped[m]) {
      grouped[m] = { create: false, read: false, update: false, delete: false, export: false, approve: false, bulk: false }
    }
    grouped[m][a] = true
    if (a === 'batch') grouped[m].bulk = true
  }
  for (const [m, actions] of Object.entries(grouped)) {
    modulePermissions[m] = [{
      id: `rbac-${m}`,
      roleId: primaryRoleCode,
      roleCode: primaryRoleCode,
      module: m,
      actions: actions as ModulePermission['actions'],
      dataScope: 'ALL',
      updatedAt: new Date().toISOString(),
      updatedBy: 'rbac',
    }]
  }
  // 字段权限 (并集)
  const fieldPerms: FieldRule[] = []
  for (const code of roleCodes) {
    for (const fr of fieldPermissionStore.getRulesForRole(code)) {
      if (!fieldPerms.find(x => x.id === fr.id)) fieldPerms.push(fr)
    }
  }

  // 后端只下发权威 id 范围；旧的中心/工厂/部门名称数组不再从模拟角色推导。
  // 业务数据的最终过滤始终由后端 @DataScope 完成。
  const dataScope: CurrentUserDataScope = {
    centers: [], factories: [], depts: [], legalEntities: [],
    orgIds: [...(authUser?.scopeOrgIds || [])],
    empIds: [...(authUser?.scopeEmpIds || [])],
  }

  ensureBreakGlassState()
  const bg = bgState.active

  return {
    empNo,
    empName,
    primaryRoleCode,
    roleCodes,
    assignments,
    modulePermissions,
    fieldPermissions: fieldPerms,
    dataScope,
    tempGrants,
    isBreakGlassActive: !!bg,
    breakGlass: bg,
    cacheVersion: state.cacheVersion,
  }
})

// ─── 公开 API ─────────────────────────────────────────────────────────────
export function useCurrentUser(): CurrentUserContext {
  return context.value
}

export function refreshUserContext(): void {
  bgState.fetchedForToken = null
  try { void useUserStore().fetchMe().finally(() => { state.cacheVersion++ }) } catch { /* Pinia 未初始化 */ }
  state.cacheVersion++
}

// 让外部 store 变更后能触发刷新
export function bumpCacheVersion() {
  state.cacheVersion++
}

export default useCurrentUser
